/**
 * Image Cache Service for Web App
 * 
 * This service handles:
 * - Caching decrypted images in browser memory
 * - Managing cache expiration and cleanup
 * - Reducing redundant downloads from Azure Blob Storage
 */
export class ImageCacheService {
    private static instance: ImageCacheService;
    private imageCache: Map<string, { dataUrl: string; blobUrl: string; blob: Blob; expiresAt: number }> = new Map();
    private readonly CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    private readonly MAX_CACHE_SIZE = 50; // Maximum number of images to cache

    private constructor() {
        // Clean up expired cache entries every hour
        setInterval(() => {
            this.cleanupExpiredCache();
        }, 60 * 60 * 1000);
    }

    /**
     * Get singleton instance
     */
    static getInstance(): ImageCacheService {
        if (!ImageCacheService.instance) {
            ImageCacheService.instance = new ImageCacheService();
        }
        return ImageCacheService.instance;
    }

    /**
     * Get cached image if available and not expired
     */
    getCachedImage(blobName: string): string | null {
        const cached = this.imageCache.get(blobName);

        if (cached && cached.expiresAt > Date.now()) {
            console.log(`[ImageCacheService] Cache hit for ${blobName}`);

            // If we have a blob object, recreate the blob URL to ensure it's valid
            if (cached.blob && cached.blob.size > 0) {
                // Revoke the old blob URL if it exists
                if (cached.blobUrl && cached.blobUrl.startsWith('blob:')) {
                    URL.revokeObjectURL(cached.blobUrl);
                }

                // Create a new blob URL from the stored blob object
                const newBlobUrl = URL.createObjectURL(cached.blob);

                // Update the cache with the new blob URL
                this.imageCache.set(blobName, {
                    ...cached,
                    blobUrl: newBlobUrl
                });

                console.log(`[ImageCacheService] Recreated blob URL for ${blobName}: ${newBlobUrl.substring(0, 50)}...`);
                return newBlobUrl;
            }

            // Fallback to stored blob URL or data URL
            return cached.blobUrl || cached.dataUrl;
        }

        if (cached) {
            console.log(`[ImageCacheService] Cache expired for ${blobName}, removing`);
            // Revoke blob URL before removing
            if (cached.blobUrl && cached.blobUrl.startsWith('blob:')) {
                URL.revokeObjectURL(cached.blobUrl);
            }
            this.imageCache.delete(blobName);
        }

        return null;
    }

    /**
     * Cache an image with expiration
     */
    setCachedImage(blobName: string, dataUrl: string, blobUrl?: string, blob?: Blob): void {
        // If cache is full, remove oldest entries
        if (this.imageCache.size >= this.MAX_CACHE_SIZE) {
            this.evictOldestEntries();
        }

        const expiresAt = Date.now() + this.CACHE_DURATION;
        this.imageCache.set(blobName, { dataUrl, blobUrl: blobUrl || '', blob: blob || new Blob(), expiresAt });

        console.log(`[ImageCacheService] Cached image ${blobName}, expires at ${new Date(expiresAt).toISOString()}`);
    }

    /**
     * Check if image is cached and valid
     */
    isCached(blobName: string): boolean {
        const cached = this.imageCache.get(blobName);
        return cached ? cached.expiresAt > Date.now() : false;
    }

    /**
     * Remove specific image from cache
     */
    removeCachedImage(blobName: string): void {
        this.imageCache.delete(blobName);
        console.log(`[ImageCacheService] Removed ${blobName} from cache`);
    }

    /**
     * Clear all cached images
     */
    clearCache(): void {
        this.imageCache.clear();
        console.log('[ImageCacheService] Cache cleared');
    }

    /**
     * Get cache statistics
     */
    getCacheStats(): { size: number; maxSize: number; entries: string[] } {
        return {
            size: this.imageCache.size,
            maxSize: this.MAX_CACHE_SIZE,
            entries: Array.from(this.imageCache.keys())
        };
    }

    /**
     * Clean up expired cache entries
     */
    private cleanupExpiredCache(): void {
        const now = Date.now();
        let removedCount = 0;

        for (const [blobName, cached] of Array.from(this.imageCache.entries())) {
            if (cached.expiresAt <= now) {
                // Revoke blob URL to free memory
                if (cached.blobUrl && cached.blobUrl.startsWith('blob:')) {
                    URL.revokeObjectURL(cached.blobUrl);
                }
                this.imageCache.delete(blobName);
                removedCount++;
            }
        }

        if (removedCount > 0) {
            console.log(`[ImageCacheService] Cleaned up ${removedCount} expired cache entries`);
        }
    }

    /**
     * Evict oldest cache entries when cache is full
     */
    private evictOldestEntries(): void {
        const entries = Array.from(this.imageCache.entries());
        // Sort by expiration time (oldest first)
        entries.sort((a, b) => a[1].expiresAt - b[1].expiresAt);

        // Remove oldest 25% of entries
        const toRemove = Math.ceil(entries.length * 0.25);
        for (let i = 0; i < toRemove; i++) {
            const [blobName, cached] = entries[i];
            // Revoke blob URL to free memory
            if (cached.blobUrl && cached.blobUrl.startsWith('blob:')) {
                URL.revokeObjectURL(cached.blobUrl);
            }
            this.imageCache.delete(blobName);
        }

        console.log(`[ImageCacheService] Evicted ${toRemove} oldest cache entries`);
    }
}
